/**
 * FWI Danger Classification — National 5-Class Breaks (FWI2025).
 *
 * Single canonical source of truth lives in PostGIS table
 * `intellifire.fire_danger_classification` (see migration 033). This module
 * is a frontend mirror: `initFWIClassifications()` is called once on app
 * boot to load the array from `/api/fire-danger-classes`. There are no
 * hardcoded defaults - if the load fails, the app is broken-loud rather
 * than silently rendering wrong classifications. Wrong is worse than
 * broken for canonical data.
 *
 * @see https://github.com/intellifire/intellifire_easymap3/issues/574
 */

export type FWIDangerClass = 'low' | 'medium' | 'high' | 'very-high' | 'extreme';

export interface FWIClassification {
  label: string;
  cssClass: string;
  dangerClass: FWIDangerClass;
  color: string;
  minFWI: number;
  /** Upper bound, exclusive on next class's minFWI. `null` for the top band. */
  maxFWI: number | null;
}

let _classifications: FWIClassification[] | null = null;

/**
 * The currently loaded classification array. Throws if `init` has not
 * succeeded yet - callers should not catch this; it signals broken setup.
 */
export function FWI_CLASSIFICATIONS(): FWIClassification[] {
  if (!_classifications) {
    throw new Error('FWI classifications not loaded - call initFWIClassifications() at app boot before rendering FWI consumers');
  }
  return _classifications;
}

interface ServerRow {
  sort_order: number;
  class_id: FWIDangerClass;
  label: string;
  min_fwi: number;
  color_hex: string;
}

function rowsToClassifications(rows: ServerRow[]): FWIClassification[] {
  const sorted = [...rows].sort((a, b) => a.sort_order - b.sort_order);
  return sorted.map((row, i) => ({
    dangerClass: row.class_id,
    label: row.label,
    cssClass: `fwi-${row.class_id}`,
    color: row.color_hex,
    minFWI: row.min_fwi,
    maxFWI: i + 1 < sorted.length ? sorted[i + 1].min_fwi : null,
  }));
}

/**
 * Load FWI classifications from the backend. Rethrows on any failure
 * (network, parse, empty list) so the boot path can surface it loudly
 * rather than letting consumers render the wrong colours.
 *
 * @param fetcher Override for tests. Defaults to fetching the API endpoint.
 */
export async function initFWIClassifications(
  fetcher?: () => Promise<ServerRow[]>,
): Promise<void> {
  const rows = fetcher
    ? await fetcher()
    : await fetch('/api/fire-danger-classes', { credentials: 'include' })
        .then(r => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json() as Promise<ServerRow[]>;
        });
  if (!Array.isArray(rows) || rows.length === 0) {
    throw new Error('initFWIClassifications: backend returned no rows for fire_danger_classification');
  }
  _classifications = rowsToClassifications(rows);
}

/**
 * Test-only: replace the in-memory classification array.
 */
export function _setFWIClassificationsForTest(rows: FWIClassification[]): void {
  _classifications = rows;
}

/**
 * Test-only: clear the cache so a fresh init or unloaded-state test can run.
 */
export function _resetFWIClassificationsForTest(): void {
  _classifications = null;
}

/**
 * Classify an FWI value. Throws if classifications haven't been loaded yet
 * (broken setup) or if a non-negative FWI doesn't match any band (cache
 * is incomplete - silently returning the lowest band would mis-classify).
 * Returns null for missing input (null / undefined / NaN) so callers can
 * decide what to render when there's simply no FWI to show.
 */
export function getFWIDangerClass(fwi: number | null | undefined): FWIClassification | null {
  if (fwi == null || isNaN(fwi)) return null;
  const classifications = FWI_CLASSIFICATIONS();
  for (let i = classifications.length - 1; i >= 0; i--) {
    if (fwi >= classifications[i].minFWI) return classifications[i];
  }
  throw new Error(`getFWIDangerClass: FWI ${fwi} did not match any band in fire_danger_classification cache (lowest minFWI=${classifications[0]?.minFWI})`);
}

/**
 * Color for an FWI value, or null when input is missing. Throws (via
 * getFWIDangerClass) if classifications haven't been loaded yet.
 */
export function getFWIColor(fwi: number | null | undefined): string | null {
  return getFWIDangerClass(fwi)?.color ?? null;
}
